/**
 * Une campagne d'appel — la file.
 *
 * Une liste à cocher, pas une session imposée : chaque ligne s'appelle,
 * s'ouvre (la fiche, avec la consigne en bandeau) ou reçoit son résultat
 * directement. Appeler ou ouvrir prend la ligne en main pour quelques minutes
 * (les autres la voient « en cours — Jean ») ; un résultat la libère et la
 * classe. Les « Pas de réponse » reviennent en tête de file à l'heure dite.
 *
 * L'écran se rafraîchit régulièrement : c'est une file partagée, ce que
 * les collègues font doit se voir.
 */
import { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import {
  ArrowLeft, PhoneCall, ExternalLink, ClipboardCheck, Loader2, Play, Pause, Square, Search, X,
  Users, Package, RotateCcw, Lock, BadgeCheck, Ban, Clock,
} from "lucide-react";
import api, { apiError } from "@/lib/crmApi";
import { Loader, ScoreBadge } from "@/crm/ui";
import { useAuth } from "@/context/CrmAuth";
import { useTwilio } from "@/context/TwilioVoice";
import Modal from "@/crm/components/Modal";
import CallCampaignForm from "@/crm/components/CallCampaignForm";
import { StatusPill, ObjectiveBar } from "@/crm/pages/CallCampaigns";

const CALL_RESULTS = ["Pas de réponse", "Numéro incorrect", "Intéressé", "Pas intéressé", "À rappeler", "Rendez-vous", "Demande d'informations"];
const TABS = [
  ["a_appeler", "À appeler"], ["en_cours", "En cours"], ["a_retenter", "À retenter"],
  ["joints", "Jointes"], ["rdv", "RDV"], ["termines", "Terminées"], ["ecartes", "Écartées"], ["all", "Toutes"],
];
const STATE_CLS = {
  a_appeler: "bg-indigo-50 text-indigo-700", en_cours: "bg-amber-50 text-amber-700",
  a_retenter: "bg-sky-50 text-sky-700", a_rappeler: "bg-sky-50 text-sky-700",
  joint: "bg-emerald-50 text-emerald-700", rdv: "bg-emerald-600 text-white",
  pas_interesse: "bg-slate-100 text-slate-600", injoignable: "bg-slate-100 text-slate-500",
  numero_incorrect: "bg-rose-50 text-rose-600", ecarte: "bg-slate-100 text-slate-400",
};
const CARD = "rounded-xl border border-slate-200 bg-white";
const BTN = "inline-flex items-center gap-1.5 rounded-lg border border-slate-300 px-3 py-1.5 text-[13px] text-slate-700 hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-40";

const fmtWhen = (iso) => (iso ? new Date(iso).toLocaleString("fr-FR", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" }) : "");

export default function CallCampaignDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const tw = useTwilio();
  const isAdmin = user?.role === "admin";
  const isManager = user?.role === "admin" || user?.role === "manager";
  const [c, setC] = useState(null);
  const [tab, setTab] = useState("a_appeler");
  const [q, setQ] = useState("");
  const [page, setPage] = useState(1);
  const [rows, setRows] = useState(null);
  const [resultFor, setResultFor] = useState(null);
  const [editing, setEditing] = useState(false);
  const [busy, setBusy] = useState(null);

  const loadCampaign = useCallback(() => api.get(`/call-campaigns/${id}`).then(({ data }) => setC(data))
    .catch((e) => { toast.error(apiError(e.response?.data?.detail) || "Campagne introuvable"); navigate("/campagnes/appel"); }), [id, navigate]);
  const loadRows = useCallback(() => api.get(`/call-campaigns/${id}/items`, { params: { tab, page, q: q || undefined, limit: 50 } })
    .then(({ data }) => setRows(data)).catch(() => setRows({ items: [], total: 0, pages: 1 })), [id, tab, page, q]);

  useEffect(() => { loadCampaign(); }, [loadCampaign]);
  useEffect(() => { loadRows(); }, [loadRows]);
  useEffect(() => { setPage(1); }, [tab, q]);
  // File partagée : ce que font les collègues doit se voir sans recharger.
  useEffect(() => {
    const t = setInterval(() => { loadRows(); loadCampaign(); }, 15000);
    return () => clearInterval(t);
  }, [loadRows, loadCampaign]);

  const refresh = () => { loadRows(); loadCampaign(); };

  const take = async (item) => {
    const { data } = await api.post(`/call-campaigns/${id}/items/${item.id}/take`);
    return data;
  };
  const call = async (item) => {
    setBusy(item.id);
    try {
      await take(item);
      if (item.telephone && tw?.makeCall) tw.makeCall(item.telephone, { company_id: item.company_id, company_name: item.nom });
      else toast.info("Softphone indisponible — composez le numéro, puis saisissez le résultat.");
      refresh();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setBusy(null); }
  };
  const openFiche = async (item) => {
    try { if (item.takeable) await take(item); } catch { /* la fiche reste consultable */ }
    navigate(`/entreprises/${item.company_id}?campagne_appel=${id}`);
  };
  const release = async (item) => {
    try { await api.post(`/call-campaigns/${id}/items/${item.id}/release`); refresh(); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };
  const adminAct = async (verb, ok) => {
    setBusy(verb);
    try {
      const { data } = await api.post(`/call-campaigns/${id}/${verb}`);
      toast.success(typeof ok === "function" ? ok(data) : ok);
      refresh();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setBusy(null); }
  };

  if (!c) return <Loader />;
  const s = c.stats;
  const active = c.status === "active";
  // Même règle que le serveur (`take`), lue dans la réponse : plage horaire,
  // quota du jour. L'écran l'annonce au lieu de laisser cliquer pour un 400.
  const dispo = rows?.availability || c.availability || { ok: active, reason: "" };
  const canAct = active && dispo.ok;

  return (
    <div className="mx-auto max-w-[1320px]" data-testid="call-campaign-detail">
      <button onClick={() => navigate("/campagnes/appel")} className="mb-4 flex items-center gap-1.5 text-sm text-slate-500 hover:text-slate-900">
        <ArrowLeft size={15} /> Campagnes d'appel
      </button>

      {/* En-tête */}
      <div className={`${CARD} mb-4 p-5`}>
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div className="min-w-0">
            <div className="flex flex-wrap items-center gap-2">
              <h1 className="font-heading text-2xl font-bold text-slate-900">{c.name}</h1>
              <StatusPill status={c.status} />
            </div>
            <div className="mt-1.5 flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-slate-500">
              <span className="inline-flex items-center gap-1"><Users size={12} />{c.assignee_names?.join(", ") || "Personne d'affecté"}</span>
              {c.solution_nom && <span className="inline-flex items-center gap-1"><Package size={12} />Proposer : <b className="text-slate-700">{c.solution_nom}</b></span>}
              <span className="inline-flex items-center gap-1"><RotateCcw size={12} />{c.retry?.max_attempts} tentatives, {c.retry?.delay_hours} h entre deux</span>
              <span>Ordre : {c.order_label}</span>
              {c.hours_label && <span className="inline-flex items-center gap-1"><Clock size={12} />{c.hours_label}</span>}
              {c.quota_per_day > 0 && <span>Quota : {c.quota_per_day} appels / jour / commercial{dispo.quota?.quota ? ` (vous : ${dispo.quota.today})` : ""}</span>}
            </div>
            {c.consigne && (
              <div data-testid="cc-consigne" className="mt-3 max-w-[80ch] whitespace-pre-line rounded-lg border border-indigo-100 bg-indigo-50/60 px-3 py-2 text-sm text-indigo-900">
                {c.consigne}
              </div>
            )}
          </div>
          {isAdmin && (
            <div className="flex flex-wrap gap-1.5">
              {c.status === "draft" && (
                <button data-testid="cc-launch" disabled={busy === "launch"}
                  onClick={() => adminAct("launch", (d) => `Lancée : ${d.inserted} entreprise(s) à appeler`)}
                  className="inline-flex items-center gap-1.5 rounded-lg bg-emerald-700 px-3 py-1.5 text-[13px] font-medium text-white hover:bg-emerald-800 disabled:opacity-50">
                  <Play size={14} /> Lancer la campagne
                </button>
              )}
              {c.status === "active" && <button onClick={() => adminAct("pause", "En pause")} className={BTN}><Pause size={14} /> Pause</button>}
              {c.status === "paused" && <button onClick={() => adminAct("resume", "Reprise")} className={BTN}><Play size={14} /> Reprendre</button>}
              {(c.status === "active" || c.status === "paused") && <button onClick={() => adminAct("close", "Clôturée — bilan conservé")} className={BTN}><Square size={14} /> Clôturer</button>}
              <button onClick={() => setEditing(true)} className={BTN}>Réglages</button>
            </div>
          )}
        </div>

        {s && (
          <div className="mt-5 grid gap-4 border-t border-slate-100 pt-4 lg:grid-cols-[minmax(0,1fr)_minmax(0,1.4fr)]">
            <ObjectiveBar stats={s} />
            <div className="grid grid-cols-3 gap-1.5 sm:grid-cols-6">
              {[["À appeler", s.a_appeler, "a_appeler"], ["En cours", s.en_cours, "en_cours"], ["À retenter", s.a_retenter, "a_retenter"],
                ["Jointes", s.joints, "joints"], ["RDV", s.rdv, "rdv"], ["Terminées", s.termines, "termines"]].map(([l, v, key]) => (
                <button key={key} onClick={() => setTab(key)} data-testid={`cc-stat-${key}`}
                  className={`rounded-lg px-1 py-1.5 text-center ${tab === key ? "bg-indigo-50 ring-1 ring-indigo-200" : "bg-slate-50 hover:bg-slate-100"}`}>
                  <div className="font-mono text-sm font-semibold text-slate-800">{v}</div>
                  <div className="text-[10.5px] text-slate-500">{l}</div>
                </button>
              ))}
            </div>
          </div>
        )}
        {!s && (
          <p className="mt-4 border-t border-slate-100 pt-4 text-sm text-slate-500">
            Pas encore lancée. Au lancement, la cible est figée : les entreprises correspondant aux filtres, avec un numéro, ni clientes ni en opposition, et non suivies par un commercial hors campagne.
          </p>
        )}
      </div>

      {/* Par commercial */}
      {s && isManager && Object.keys(s.par_commercial || {}).length > 0 && (
        <div className={`${CARD} mb-4 p-4`} data-testid="cc-par-commercial">
          <h3 className="mb-2 font-heading text-[15px] font-bold text-slate-900">Par commercial</h3>
          <table className="w-full text-sm">
            <thead className="text-xs uppercase text-slate-500"><tr><th className="py-1 text-left">Commercial</th><th className="py-1 text-right">Appels</th><th className="py-1 text-right">Jointes</th><th className="py-1 text-right">RDV</th><th className="py-1 text-right">Taux joint</th></tr></thead>
            <tbody>
              {Object.entries(s.par_commercial).map(([uid, v]) => (
                <tr key={uid} className="border-t border-slate-100">
                  <td className="py-1.5">{v.name || uid}</td>
                  <td className="py-1.5 text-right font-mono">{v.appels || 0}</td>
                  <td className="py-1.5 text-right font-mono">{v.joints || 0}</td>
                  <td className="py-1.5 text-right font-mono">{v.rdv || 0}</td>
                  <td className="py-1.5 text-right font-mono">{v.appels ? Math.round(((v.joints || 0) * 100) / v.appels) : 0} %</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {s && active && !dispo.ok && (
        <div data-testid="cc-unavailable" className="mb-4 flex items-center gap-2 rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800">
          <Clock size={16} className="shrink-0" /> {dispo.reason} — la file reste consultable, les appels reprendront quand la règle le permettra.
        </div>
      )}

      {/* La file */}
      {s && (
        <div className={`${CARD} overflow-hidden`}>
          <div className="flex flex-wrap items-center gap-2 border-b border-slate-100 p-3">
            <div className="flex flex-wrap gap-1 rounded-xl bg-slate-100 p-1" role="tablist">
              {TABS.map(([key, label]) => (
                <button key={key} role="tab" aria-selected={tab === key} data-testid={`cc-tab-${key}`} onClick={() => setTab(key)}
                  className={`rounded-lg px-2.5 py-1 text-[13px] ${tab === key ? "bg-white font-semibold text-slate-900 shadow-sm" : "text-slate-600 hover:text-slate-900"}`}>
                  {label}
                </button>
              ))}
            </div>
            <div className="ml-auto flex min-w-[200px] items-center gap-2 rounded-lg border border-slate-300 px-3 py-1.5">
              <Search size={14} className="text-slate-400" />
              <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Nom, ville, numéro…" data-testid="cc-search"
                className="min-w-0 flex-1 bg-transparent text-sm outline-none" />
              {q && <button onClick={() => setQ("")} aria-label="Effacer"><X size={13} className="text-slate-400" /></button>}
            </div>
          </div>

          {!rows ? <Loader /> : rows.items.length === 0 ? (
            <p className="px-4 py-10 text-center text-sm text-slate-400" data-testid="cc-empty">
              {tab === "a_appeler" ? "Rien à appeler pour l'instant — les retentatives reviendront à l'heure dite." : "Aucune ligne ici."}
            </p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-slate-50 text-xs uppercase text-slate-500">
                  <tr>
                    <th className="p-3 text-left font-semibold">Entreprise</th>
                    <th className="p-3 text-left font-semibold">Téléphone</th>
                    <th className="p-3 text-left font-semibold">Score</th>
                    <th className="p-3 text-left font-semibold">État</th>
                    <th className="p-3 text-left font-semibold">Dernier appel</th>
                    <th className="p-3 text-right font-semibold">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {rows.items.map((it) => (
                    <tr key={it.id} data-testid={`cc-item-${it.id}`} className={it.locked && !it.lock_mine ? "bg-amber-50/40" : "hover:bg-slate-50"}>
                      <td className="p-3">
                        <div className="flex items-center gap-2">
                          <span className="font-medium text-slate-800">{it.nom}</span>
                          {it.client && <span className="rounded bg-emerald-600 px-1.5 py-0.5 text-[10px] font-semibold text-white"><BadgeCheck size={10} className="mr-0.5 inline" />CLIENT</span>}
                          {it.excluded && <span className="rounded bg-rose-100 px-1.5 py-0.5 text-[10px] font-semibold text-rose-700"><Ban size={10} className="mr-0.5 inline" />OPPOSITION</span>}
                        </div>
                        <div className="text-xs text-slate-400">{[it.secteur, it.ville].filter(Boolean).join(" · ")}</div>
                      </td>
                      <td className="p-3 font-mono text-[13px] text-slate-700">{it.telephone || <span className="text-slate-300">—</span>}</td>
                      <td className="p-3"><ScoreBadge score={it.lead_score} category={it.score_category} /></td>
                      <td className="p-3">
                        <span className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-medium ${STATE_CLS[it.effective_state] || "bg-slate-100 text-slate-600"}`}>
                          {it.effective_state === "en_cours" && <Lock size={11} />}
                          {it.effective_state === "en_cours" ? `En cours — ${it.lock_user_name || "?"}` : it.state_label}
                        </span>
                        {it.attempts > 0 && <div className="mt-0.5 text-[11px] text-slate-400">{it.attempts} tentative{it.attempts > 1 ? "s" : ""}{it.next_attempt_at && it.effective_state !== "a_appeler" && it.state !== "injoignable" ? ` · retour ${fmtWhen(it.next_attempt_at)}` : ""}</div>}
                      </td>
                      <td className="p-3 text-xs text-slate-500">
                        {it.last_result ? <>{it.last_result}<div className="text-[11px] text-slate-400">{fmtWhen(it.last_called_at)} · {it.last_called_by_name}</div></> : "—"}
                      </td>
                      <td className="p-3">
                        <div className="flex justify-end gap-1.5">
                          <button data-testid={`cc-call-${it.id}`} disabled={!canAct || !it.takeable || !it.telephone || busy === it.id} onClick={() => call(it)}
                            title={!it.takeable ? "Ligne indisponible" : "Appeler (prend la ligne en main)"}
                            className="inline-flex items-center gap-1.5 rounded-lg bg-emerald-700 px-2.5 py-1.5 text-[13px] font-medium text-white hover:bg-emerald-800 disabled:cursor-not-allowed disabled:opacity-40">
                            {busy === it.id ? <Loader2 size={14} className="animate-spin" /> : <PhoneCall size={14} />}
                          </button>
                          <button data-testid={`cc-open-${it.id}`} onClick={() => openFiche(it)} title="Ouvrir la fiche (consigne en bandeau)" className={`${BTN} px-2.5`}><ExternalLink size={14} /></button>
                          <button data-testid={`cc-result-${it.id}`} disabled={!canAct || !it.takeable} onClick={() => setResultFor(it)} title="Saisir le résultat" className={`${BTN} px-2.5`}><ClipboardCheck size={14} /></button>
                          {it.lock_mine && <button onClick={() => release(it)} title="Libérer la ligne" className={`${BTN} px-2`}><X size={14} /></button>}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {rows && rows.pages > 1 && (
            <div className="flex items-center justify-center gap-2 border-t border-slate-100 p-3">
              <button disabled={page <= 1} onClick={() => setPage((p) => p - 1)} className={BTN}>Précédent</button>
              <span className="text-sm text-slate-500">Page {page} / {rows.pages}</span>
              <button disabled={page >= rows.pages} onClick={() => setPage((p) => p + 1)} className={BTN}>Suivant</button>
            </div>
          )}
        </div>
      )}

      {resultFor && <ResultModal campaignId={id} item={resultFor} onClose={() => setResultFor(null)} onSaved={() => { setResultFor(null); refresh(); }} />}
      {editing && <CallCampaignForm campaign={c} onClose={() => setEditing(false)} onSaved={() => { setEditing(false); refresh(); }} />}
    </div>
  );
}

/** Résultat saisi depuis la file — même journal d'appel que depuis la fiche. */
function ResultModal({ campaignId, item, onClose, onSaved }) {
  const [result, setResult] = useState("");
  const [note, setNote] = useState("");
  const [nextDate, setNextDate] = useState("");
  const [saving, setSaving] = useState(false);
  const save = async () => {
    if (!result) return toast.error("Choisissez un résultat");
    setSaving(true);
    try {
      const { data } = await api.post(`/call-campaigns/${campaignId}/items/${item.id}/result`, {
        result, description: note, next_action_date: nextDate || null,
      });
      toast.success(`${item.nom} : ${data.item.state_label}`);
      onSaved();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };
  return (
    <Modal onClose={onClose} size="md" label="Résultat de l'appel" testid="cc-result-modal">
      <div className="mb-3 flex items-center justify-between">
        <h3 className="font-heading text-base font-bold text-slate-900">{item.nom}</h3>
        <button onClick={onClose} aria-label="Fermer"><X size={18} /></button>
      </div>
      {item.telephone && <div className="mb-3 rounded-lg bg-indigo-50/70 px-3 py-2 font-mono text-sm text-indigo-700">{item.telephone}</div>}
      <div className="grid grid-cols-2 gap-2">
        {CALL_RESULTS.map((r) => (
          <button key={r} data-testid={`cc-result-${r}`} onClick={() => setResult(r)}
            className={`rounded-xl border py-2.5 text-[12.5px] font-medium ${result === r ? "border-indigo-500 bg-indigo-50/60 text-indigo-700" : "border-slate-200 text-slate-600"}`}>{r}</button>
        ))}
      </div>
      {result === "À rappeler" && (
        <div className="mt-3">
          <label className="mb-1 block text-xs text-slate-500" htmlFor="cc-next-date">Rappeler le</label>
          <input id="cc-next-date" type="datetime-local" value={nextDate} onChange={(e) => setNextDate(e.target.value)}
            className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm outline-none focus:border-indigo-500" />
          <p className="mt-1 text-[11.5px] text-slate-400">Sans date : la ligne revient au délai standard de la campagne.</p>
        </div>
      )}
      <textarea rows={2} value={note} onChange={(e) => setNote(e.target.value)} placeholder="Note (facultatif)"
        className="mt-3 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm outline-none focus:border-indigo-500" />
      <div className="mt-4 flex justify-end gap-2">
        <button onClick={onClose} className={BTN}>Annuler</button>
        <button data-testid="cc-result-save" disabled={saving} onClick={save}
          className="inline-flex items-center gap-2 rounded-lg bg-indigo-600 px-4 py-2 text-sm font-medium text-white hover:bg-indigo-700 disabled:opacity-50">
          {saving && <Loader2 size={14} className="animate-spin" />} Enregistrer
        </button>
      </div>
    </Modal>
  );
}
